import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import _debounce from 'lodash/debounce';
import { captureExceptionEvent } from '@blocs.observability';
import { PLACES_DEBOUNCE_MS, PLACES_MIN_QUERY_LENGTH } from './constants';
import { mapPlaceToAddress } from './mapPlaceToAddress';
import { PlaceSuggestion, SuggestedAddress } from './types';

const mapSuggestion = (suggestion: google.maps.places.PlacePrediction): PlaceSuggestion | null => {
  if (!suggestion) {
    return null;
  }

  return {
    id: suggestion.placeId,
    text: suggestion.text.text,
    matches: suggestion.text.matches.map(({ startOffset, endOffset }) => ({ startOffset, endOffset })),
  };
};

// when setting up _debounce it needs to be done OUTSIDE the component or a new instance of the debounced fn
// is created on every render
type SuggestionSearchParams = {
  lib: google.maps.PlacesLibrary;
  sessionToken: google.maps.places.AutocompleteSessionToken;
  searchValue: string;
  regions: string[];
  setIsLoading: (isLoading: boolean) => void;
  setSuggestions: (suggestions: google.maps.places.PlacePrediction[]) => void;
};

// debounce wrapper around the function implementation
const searchForSuggestions = _debounce(
  async ({ lib, sessionToken, searchValue, regions, setIsLoading, setSuggestions }: SuggestionSearchParams) => {
    // if we don't have the places library, a session token, or a valid search input
    // then return early
    const searchVal = searchValue?.trim();
    if (!lib || !sessionToken || searchVal?.length < PLACES_MIN_QUERY_LENGTH) {
      return;
    }

    setIsLoading(true);
    let suggestions: google.maps.places.PlacePrediction[] = [];

    try {
      const request: google.maps.places.AutocompleteRequest = {
        input: searchVal,
        sessionToken,
        ...(regions?.length ? { includedRegionCodes: regions } : {}),
      };

      const result = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      suggestions = (result?.suggestions || [])?.filter(Boolean)?.map((sug) => sug?.placePrediction);
    } catch (err) {
      captureExceptionEvent(err instanceof Error ? err : new Error(String(err)), {
        location: 'usePlacesAutocomplete',
      });
    }
    setIsLoading(false);
    setSuggestions(suggestions);
  },
  PLACES_DEBOUNCE_MS
);

/**
 * Debounced, session-tokened Google Places "Autocomplete Suggestions" (New)
 * lookup. Not tied to any particular input component — feed it the current
 * text value and it returns the matching suggestions, driven by whichever
 * `<PlacesApiProvider>` ancestor loaded the "places" library.
 */
export const usePlacesAutocomplete = ({ regions = [] }: { regions?: string[] }) => {
  const placesLib = useMapsLibrary('places');
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<google.maps.places.PlacePrediction[]>([]);

  // cancel the debounced search when the hook unmounts if there is one pending
  useEffect(() => {
    return () => {
      // if the debounce function is still waiting to run, cancel it when the component unmounts
      searchForSuggestions?.cancel();
    };
  }, []);

  // generate a session token if one is not set
  useEffect(() => {
    if (!placesLib) {
      return;
    }

    if (!sessionToken) {
      setSessionToken(new placesLib.AutocompleteSessionToken());
    }
  }, [placesLib, sessionToken]);

  const getAddressForSuggestion = useCallback(
    async ({ placeId }: { placeId: string }): Promise<SuggestedAddress> => {
      const suggestion = suggestions.find((sug) => sug.placeId === placeId);
      const place = suggestion?.toPlace?.();
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
      const address = mapPlaceToAddress(place);
      setSessionToken(null);
      setSuggestions([]);
      return address;
    },
    [suggestions]
  );

  return useMemo(() => {
    return {
      isLoading,
      searchForSuggestions: (newValue: string) => {
        searchForSuggestions({
          lib: placesLib,
          sessionToken,
          searchValue: newValue,
          regions,
          setIsLoading,
          setSuggestions,
        });
      },
      getAddressForSuggestion,
      suggestions: suggestions?.map((sug) => mapSuggestion(sug)),
    };
  }, [placesLib, sessionToken, regions, isLoading, suggestions, getAddressForSuggestion]);
};
